/**
 * Inline game chat — the primary way to chat during a game.
 *
 * The 💬 button on the map controls bar opens GameChatInputBar directly beneath
 * that bar, styled to match it (same paper fill, olive rule, bar height, with a
 * narrower bordered field). The log below stays the normal log; sent messages
 * appear in it like any other line.
 *
 * Portrait with the native keyboard up, GameMapHost slides the map controls
 * bar + this input bar up (a transform, timed with the keyboard) so the input
 * sits on the keyboard; nothing else moves into view.
 *
 * ⚠️ The input bar keeps ONE position in GameMapHost's tree — moving a
 * focused native TextInput drops the iOS keyboard.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { FlatList, Platform, Text, View, useWindowDimensions, type TextInput } from 'react-native'
import { isCoarsePointerWeb } from '../../lib/platform'
import type { ChatMessageResponse, GamePlayerResponse } from '@warcarp/api-types'
import { usePostMessage } from '../../hooks/useChat'
import type { GameState } from '../../lib/gameState'
import { applyLogFilters, DEFAULT_LOG_FILTERS, type ActivityFilterState } from '../../lib/activityFilters'
import { chatOnlyLogRows } from '../../lib/inlineChat'
import { ChatComposerInput } from './ChatComposerInput'
import { ChatLogRow, useChatSeenViewability } from './ChatLogRow'
import { recentActivitySidebarWidth } from './RecentActivitySidebar'
import { DESKTOP_BREAKPOINT } from '../../lib/mapDimensions'
import type { LogRow } from '../../lib/logFeed'
import { useTheme } from '../../hooks/useTheme'
import { useChatComposerExpansion } from '../../hooks/useChatComposerExpansion'
import { ChatComposerAccessory } from './ChatComposerAccessory'
import { GAME_CONTROL_EDGE_INSET } from '../../lib/gameChrome'

/** How long a lost focus must persist before the keyboard layout reacts. */
export const CHAT_BLUR_SETTLE_MS = 200

/** Matches MapControlsBar's height when it carries edge accessories. */
export const CHAT_INPUT_BAR_HEIGHT = 44

// Unsent inline-chat text per game. Lives outside the component so a draft
// survives the bar unmounting (chat closed, turn change, re-render of the
// screen) for the rest of the session.
const chatDrafts = new Map<string, string>()

/** Whether this game's inline chat has unsent text. */
export function hasChatDraft(gameId: string): boolean {
  return (chatDrafts.get(gameId) ?? '').trim().length > 0
}

export function GameChatInputBar({
  gameId,
  onFocusChange,
  above,
  leading,
  trailing,
  below,
  topStrip,
  bottomStrip,
  inputHidden = false,
  roomy = false,
  bottomRule = true,
  expandOnFocus = false,
}: {
  gameId: string
  onFocusChange: (focused: boolean) => void
  /** Rows above the input inside the bar (wide layouts: the log controls row
   *  and, when open, the log filters). */
  above?: React.ReactNode
  /** On the input's line, left of it (portrait: ALL | CHAT and filters). */
  leading?: React.ReactNode
  /** On the input's line, right of send (portrait: grow). */
  trailing?: React.ReactNode
  /** Under the input's line (portrait: the log filters, when open). */
  below?: React.ReactNode
  /** Full-width status strip, outside the composer's padding (below the map). */
  topStrip?: React.ReactNode
  /** Full-width status strip below the desktop composer. */
  bottomStrip?: React.ReactNode
  /** The log filters are open: the input and send give way to them. */
  inputHidden?: boolean
  /** Desktop/tablet: more vertical padding and a light rule on top, setting
   *  the input off from the turn controls above it. */
  roomy?: boolean
  /** Its own bottom border. Portrait drops it: the list beneath draws the
   *  line (two stacked rules read as a heavier one). */
  bottomRule?: boolean
  /** The parent supplies its keyboard-stable portrait decision. */
  expandOnFocus?: boolean
}) {
  const { theme } = useTheme()
  const roomyRule = theme === 'dark' ? 'rgba(138, 161, 74, 0.35)' : 'rgba(74, 90, 42, 0.3)'
  const inputRef = useRef<TextInput>(null)
  const expansion = useChatComposerExpansion(expandOnFocus && (Platform.OS !== 'web' || isCoarsePointerWeb()))
  const { onFocus: expandComposer, onBlur: collapseComposer } = expansion
  const [text, setTextState] = useState(() => chatDrafts.get(gameId) ?? '')
  const setText = useCallback((next: string) => {
    if (next) chatDrafts.set(gameId, next)
    else chatDrafts.delete(gameId)
    setTextState(next)
  }, [gameId])
  const [sendError, setSendError] = useState<string | null>(null)
  const postMessage = usePostMessage(gameId)

  // Report focus loss a beat late: a blur during a tap (e.g. pressing Send)
  // must not re-lay out the screen before the press lands. A refocus within
  // the window cancels it.
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
  }, [])
  const handleFocus = useCallback(() => {
    expandComposer()
    if (blurTimer.current) {
      clearTimeout(blurTimer.current)
      blurTimer.current = null
    }
    onFocusChange(true)
  }, [onFocusChange, expandComposer])
  const handleBlur = useCallback(() => {
    collapseComposer()
    if (blurTimer.current) clearTimeout(blurTimer.current)
    blurTimer.current = setTimeout(() => {
      blurTimer.current = null
      onFocusChange(false)
    }, CHAT_BLUR_SETTLE_MS)
  }, [onFocusChange, collapseComposer])

  const handleSend = useCallback(async () => {
    const trimmed = text.trim()
    if (!trimmed || postMessage.isPending) return
    setSendError(null)
    setText('')
    // Phones / touch browsers: the press has landed, so let the keyboard close
    // now while the message posts (one smooth transition back to the full map).
    // Desktop keeps focus for the next message.
    if (Platform.OS !== 'web' || isCoarsePointerWeb()) inputRef.current?.blur()
    try {
      await postMessage.mutateAsync({ content: trimmed })
    } catch {
      // The React Query layer already reports mutation failures; restore the
      // text so the line isn't lost and say so inline.
      setSendError("Couldn't send — please try again.")
      setText(trimmed)
    }
  }, [postMessage, text, setText])

  return (
    <>
    {topStrip}
    <View
      testID="inline-chat-bar"
      accessibilityLabel="Game chat"
      className={`bg-paper ${bottomRule ? 'border-b border-olive' : ''}`}
      // 8 px inset = the top bar's menu button and the turn panels, so all
      // three share one left edge.
      style={{
        minHeight: CHAT_INPUT_BAR_HEIGHT,
        paddingHorizontal: GAME_CONTROL_EDGE_INSET,
        paddingVertical: roomy ? 8 : 4,
        justifyContent: 'center',
        // The unread bubble crosses the divider above this bar.
        zIndex: 1,
        ...(roomy ? { borderTopWidth: 1, borderTopColor: roomyRule } : null),
      }}
    >
      {sendError && (
        <Text className="text-blood font-typed" style={{ fontSize: 11, paddingBottom: 2 }}>{sendError}</Text>
      )}
      {above ? <View style={{ width: '100%', maxWidth: 560, alignSelf: 'center' }}>{above}</View> : null}
      <View style={{ width: '100%', maxWidth: 560, alignSelf: 'center', flexDirection: 'row', alignItems: 'center' }}>
        {leading ? <ChatComposerAccessory progress={expansion.progress} duration={expansion.duration} hidden={expansion.expanded}>{leading}</ChatComposerAccessory> : null}
        {inputHidden ? <View style={{ flex: 1 }} /> : (
        <View style={{ flex: 1 }}>
        <ChatComposerInput
          variant="bar"
          inputRef={inputRef}
          testID="inline-chat-input"
          value={text}
          onChangeText={(next) => {
            setText(next)
            if (sendError) setSendError(null)
          }}
          onSend={() => { void handleSend() }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Message…"
          isSending={postMessage.isPending}
        />
        </View>
        )}
        {trailing ? <ChatComposerAccessory progress={expansion.progress} duration={expansion.duration} hidden={expansion.expanded} gap={0}><View style={{ paddingLeft: 12 }}>{trailing}</View></ChatComposerAccessory> : null}
      </View>
      {below ? <View style={{ width: '100%', maxWidth: 560, alignSelf: 'center', paddingTop: 6 }}>{below}</View> : null}
    </View>
    {bottomStrip}
    </>
  )
}

/**
 * Your turn with chat open: the chat-only log (newest first, log-line style)
 * that takes over the turn-controls area beneath the chat bar. Same container
 * shape as RecentActivitySidebar so the layout doesn't jump.
 */
export function ChatLogList({
  messages,
  state,
  players,
  orientation,
  topContent,
  filters = DEFAULT_LOG_FILTERS,
}: {
  messages: ChatMessageResponse[]
  state: GameState
  players: GamePlayerResponse[]
  orientation: 'bottom-panel' | 'left-sidebar'
  /** Landscape: the column header (incl. the chat bar) stays on top. */
  topContent?: React.ReactNode
  /** The log filters: player and sort apply to chat (it has no kinds). */
  filters?: ActivityFilterState
}) {
  const playerBySlot = useMemo(() => {
    const map = new Map<string, GamePlayerResponse>()
    for (const player of players) map.set(String(player.slot), player)
    return map
  }, [players])
  const slotByUserId = useMemo(() => {
    const map = new Map<string, string>()
    for (const player of players) map.set(player.userId, String(player.slot))
    return map
  }, [players])
  const stateEvents = state.events
  const { playerFilter, newestFirst } = filters
  const rows = useMemo(
    () => applyLogFilters(
      chatOnlyLogRows({ messages, events: stateEvents ?? [], slotByUserId }),
      { playerFilter, kindFilter: null, newestFirst },
    ),
    [messages, stateEvents, slotByUserId, playerFilter, newestFirst],
  )
  const { width: screenWidth } = useWindowDimensions()
  const isSidebar = orientation === 'left-sidebar'
  const chatSeen = useChatSeenViewability(useId())
  const renderRow = useCallback(
    ({ item }: { item: LogRow }) => <ChatLogRow row={item} playerBySlot={playerBySlot} />,
    [playerBySlot],
  )

  return (
    <View
      testID="turn-chat-log"
      accessibilityLabel="Chat log"
      style={isSidebar ? { width: recentActivitySidebarWidth(screenWidth >= DESKTOP_BREAKPOINT) } : { flex: 1 }}
      // Portrait: a top rule under the chat input, like the full log's.
      className={isSidebar ? 'border-r border-olive bg-canvas' : 'border-t border-olive bg-canvas'}
    >
      {topContent}
      <FlatList
        className="flex-1"
        data={rows}
        keyExtractor={(row) => row.key}
        renderItem={renderRow}
        // Chat lines on screen count as read (lib/chatSeen).
        {...chatSeen}
        ItemSeparatorComponent={() => (
          <View style={{ height: 1, backgroundColor: '#3d4020', marginHorizontal: 12, opacity: 0.4 }} />
        )}
        contentContainerStyle={{ paddingVertical: 4 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        initialNumToRender={12}
        maxToRenderPerBatch={12}
        windowSize={5}
        ListEmptyComponent={
          <View className="items-center py-8 px-3">
            <Text className="font-typed text-ink-soft text-xs italic text-center">
              {playerFilter !== null ? 'No messages from this player.' : 'No messages yet. Write the first line.'}
            </Text>
          </View>
        }
      />
    </View>
  )
}
